import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldLabel,
    gradientForId,
    Input,
    Text,
    Textarea,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { ArrowDown, ArrowUp, Film } from "lucide-react";
import { COURSE_FROM_VIDEOS_MAX } from "@athena/common/utils/videoDetails";
import { CreateCourseFromVideosDocument } from "@/graphql/courses.generated";

/** A finished upload that can become a lesson. */
export interface CourseVideo {
    assetId: string;
    title: string;
    posterUrl: string | null;
}

interface BuildCourseDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** In queue order, which is the starting lesson order. */
    videos: CourseVideo[];
}

/**
 * Turns finished uploads into a draft course: one lesson per chosen video, in the order shown here.
 *
 * Draft, and opened in the course editor afterwards, because a course built in one click is a
 * starting point — modules, a cover image and publishing are the editor's job.
 */
export function BuildCourseDialog({ open, onOpenChange, videos }: BuildCourseDialogProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [
        title,
        setTitle,
    ] = useState<string>("");
    const [
        description,
        setDescription,
    ] = useState<string>("");
    /*
     * Asset ids in lesson order, and which are included. Seeded once — everything finished, in queue
     * order — and the page mounts this dialog only while it is open, so each opening starts fresh
     * while the list re-rendering underneath cannot undo the operator's ordering.
     */
    const [
        order,
        setOrder,
    ] = useState<string[]>(() => videos.map((v) => v.assetId));
    const [
        chosen,
        setChosen,
    ] = useState<Set<string>>(() => new Set(videos.map((v) => v.assetId)));

    const [
        createCourse,
        { loading },
    ] = useMutation(CreateCourseFromVideosDocument);

    const byId = new Map(
        videos.map((v) => [
            v.assetId,
            v,
        ]),
    );
    const ordered = order.flatMap((id) => {
        const video = byId.get(id);
        return video ? [video] : [];
    });
    const selectedIds = ordered.filter((v) => chosen.has(v.assetId)).map((v) => v.assetId);
    const tooMany = selectedIds.length > COURSE_FROM_VIDEOS_MAX;

    const move = (index: number, by: -1 | 1) =>
        setOrder((current) => {
            const next = [...current];
            const target = index + by;
            if (target < 0 || target >= next.length) return current;
            [
                next[index],
                next[target],
            ] = [
                next[target] as string,
                next[index] as string,
            ];
            return next;
        });

    const toggle = (assetId: string) =>
        setChosen((current) => {
            const next = new Set(current);
            if (next.has(assetId)) next.delete(assetId);
            else next.add(assetId);
            return next;
        });

    const submit = async () => {
        try {
            const { data } = await createCourse({
                variables: {
                    input: {
                        title: title.trim(),
                        description: description.trim() || undefined,
                        videoAssetIds: selectedIds,
                    },
                },
                // The Courses grid lists this new course next time it loads.
                update: (cache) => {
                    cache.evict({ fieldName: "courses" });
                    cache.gc();
                },
            });
            const courseId = data?.createCourseFromVideos.id;
            if (!courseId) return;
            toast.success(t("admin.bulkUpload.buildCourseCreated", { count: selectedIds.length }));
            onOpenChange(false);
            navigate(`/course-preview/${courseId}`);
        } catch (e) {
            Sentry.captureException(e);
            toast.error(t("admin.bulkUpload.buildCourseFailed", { message: (e as Error).message }));
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
            <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-4">
                <DialogHeader>
                    <DialogTitle>{t("admin.bulkUpload.buildCourseTitle")}</DialogTitle>
                    <DialogDescription>{t("admin.bulkUpload.buildCourseDescription")}</DialogDescription>
                </DialogHeader>

                {/*
                 * Padded and pulled back out by the same amount: a scrolling box clips whatever is
                 * drawn past its edge, and that included the inputs' focus ring.
                 */}
                <div className="-m-1 flex min-h-0 flex-col gap-4 overflow-y-auto p-1">
                    <Field>
                        <FieldLabel htmlFor="build-course-title" required>
                            {t("admin.bulkUpload.courseTitle")}
                        </FieldLabel>
                        <Input
                            id="build-course-title"
                            value={title}
                            maxLength={255}
                            placeholder={t("admin.bulkUpload.courseTitlePlaceholder")}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="build-course-description">
                            {t("admin.bulkUpload.courseDescription")}
                        </FieldLabel>
                        <Textarea
                            id="build-course-description"
                            value={description}
                            rows={2}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Field>

                    <div className="flex flex-col gap-2">
                        <Text size="sm" weight="semibold">
                            {t("admin.bulkUpload.buildCourseLessons")}
                        </Text>
                        <ol className="flex flex-col gap-1.5">
                            {ordered.map((video, index) => {
                                const on = chosen.has(video.assetId);
                                const checkboxId = `build-course-${video.assetId}`;
                                return (
                                    <li
                                        key={video.assetId}
                                        className={cn(
                                            "border-border flex items-center gap-3 rounded-lg border p-2 transition-opacity",
                                            !on && "opacity-50",
                                        )}
                                    >
                                        <Checkbox
                                            id={checkboxId}
                                            checked={on}
                                            onCheckedChange={() => toggle(video.assetId)}
                                        />
                                        <Text as="span" size="xs" muted className="w-5 text-right tabular-nums">
                                            {on ? selectedIds.indexOf(video.assetId) + 1 : "–"}
                                        </Text>
                                        <span
                                            className={cn(
                                                "flex aspect-video w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-linear-to-br",
                                                !video.posterUrl && gradientForId(video.assetId),
                                            )}
                                        >
                                            {video.posterUrl ? (
                                                <img
                                                    src={video.posterUrl}
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <Film className="h-4 w-4 text-white/80" />
                                            )}
                                        </span>
                                        <label
                                            htmlFor={checkboxId}
                                            className="min-w-0 flex-1 cursor-pointer truncate text-sm"
                                        >
                                            {video.title}
                                        </label>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            disabled={index === 0}
                                            onClick={() => move(index, -1)}
                                            aria-label={`${t("admin.bulkUpload.moveUp")} ${video.title}`}
                                        >
                                            <ArrowUp className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            disabled={index === ordered.length - 1}
                                            onClick={() => move(index, 1)}
                                            aria-label={`${t("admin.bulkUpload.moveDown")} ${video.title}`}
                                        >
                                            <ArrowDown className="h-3.5 w-3.5" />
                                        </Button>
                                    </li>
                                );
                            })}
                        </ol>
                        {tooMany && (
                            <Text size="xs" className="text-destructive">
                                {t("admin.bulkUpload.buildCourseTooMany", { max: COURSE_FROM_VIDEOS_MAX })}
                            </Text>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" className="w-auto" disabled={loading} onClick={() => onOpenChange(false)}>
                        {t("common.actions.cancel")}
                    </Button>
                    <Button
                        className="w-auto max-w-none"
                        disabled={!title.trim() || selectedIds.length === 0 || tooMany}
                        loading={loading}
                        onClick={() => void submit()}
                    >
                        {t("admin.bulkUpload.buildCourseConfirm", { count: selectedIds.length })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
